import * as React from 'react';
import { Redirect } from 'react-router-dom';

import { Formik, FormikConfig } from 'formik';

import { pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import * as A from 'fp-ts/Array';
import {
  FullMatchFragment,
  useTeamPlayersQuery,
  MatchPlayerFragment,
  usePickPlayersMutation,
} from '../../generated/graphql';
import { Heading } from '../../components/Heading';
import { ChecklistItem } from '../../components/Checklist';
import { SubmitButton } from '../../components/SubmitButton';
import { useAlert } from '../../contexts/AlertContext';

import { ButtonLink } from '../../components/Button';
import { formatErrors } from '../../utils/formatErrors';

import { getSelectedKeys } from '../../utils/getSelectedKeys';
import { reduceToSelections } from '../../utils/reduceToSelections';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { AvailabilityStatusDot } from '../../components/AvailabilityStatus';
import { getPlayerListWithAvailability } from '../../utils/makeAvailabilityMap';
import { TextLink } from '../../components/ClickableText';
import { Loader } from '../../components/LoadingContainer';

import { paths } from '../../paths';
import { useConfirm } from '../../contexts/ConfirmContext';
import { PlayerAvatar } from '../../components/PlayerAvatar';
import { isNil } from '../../utils/isNil';
import { RadioChoice, ChoiceProps } from '../../components/RadioChoice';
import { byAvailability, byPlayerName } from '../../utils/playerSorting';

type Fields = Record<string, boolean>;

interface PickSquadFormProps {
  match: FullMatchFragment;
  availablePlayers: MatchPlayerFragment[];
  onSubmit: FormikConfig<Fields>['onSubmit'];
  loading: boolean;
}

const NoPlayersMessage: React.FC = () => {
  return (
    <div className="space-y-4">
      <p className="text-center">
        You can't select players for this match as you don't have any yet.
      </p>
      <p className="text-center">
        Head over to{' '}
        <TextLink to={paths.teamSettings.players.squad()}>
          team settings
        </TextLink>{' '}
        to add some players now.
      </p>
    </div>
  );
};

type SortOption = 'default' | 'availability' | 'az';

const PickSquadForm: React.FC<PickSquadFormProps> = ({
  match,
  availablePlayers,
  onSubmit,
  loading,
}) => {
  const squad = match.players.reduce<Fields>(reduceToSelections(true), {});
  const allPlayers = availablePlayers.reduce<Fields>(
    reduceToSelections(false),
    {},
  );
  const [sorting, setSorting] = React.useState<SortOption>('default');

  const playersWithAvailability = getPlayerListWithAvailability(
    match.availabilities,
    availablePlayers,
  );

  const sortedPlayers =
    sorting === 'default'
      ? playersWithAvailability
      : sorting === 'az'
      ? A.sort(byPlayerName)(playersWithAvailability)
      : pipe(
          playersWithAvailability,
          A.sort(byPlayerName),
          A.sort(byAvailability),
        );

  const initialValues = { ...allPlayers, ...squad };
  const sortOptions: ChoiceProps<SortOption>[] = [
    { value: 'default', children: 'Recently played' },
    { value: 'az', children: 'A-Z' },
    { value: 'availability', children: 'Availability' },
  ];

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({ handleChange, values, handleSubmit }) => {
        const selectedPlayerIds = getSelectedKeys(values);

        return (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex justify-between">
              <Heading level={3}>
                {selectedPlayerIds.length} players selected
              </Heading>

              <TextLink to={paths.teamSettings.players.squad()}>
                Manage team players
              </TextLink>
            </div>
            <div>
              <h3 className="mb-2 text-xs text-grey-600 ">Sort by</h3>
              <RadioChoice
                options={sortOptions}
                value={sorting}
                onChange={setSorting}
              />
            </div>

            <ul>
              {sortedPlayers.map(({ player, availability }) => (
                <li key={player.id}>
                  <ChecklistItem
                    name={player.id}
                    onChange={handleChange}
                    checked={values[player.id]}
                    value={player.id}
                  >
                    <PlayerAvatar player={player} className="mr-2" />
                    <p className="mr-2">{player.name}</p>
                    <AvailabilityStatusDot
                      className="mr-2"
                      status={availability}
                    />
                  </ChecklistItem>
                </li>
              ))}
            </ul>

            <SubmitButton loading={loading}>Confirm Squad</SubmitButton>
            <ButtonLink to={paths.match.home(match.id)} kind="plain" icon="x">
              Cancel
            </ButtonLink>
          </form>
        );
      }}
    </Formik>
  );
};

interface Props {
  match: FullMatchFragment;
}

const confirmProps = {
  title: 'Confirm Squad',
  children: (
    <p>
      Some of the selected players have indicated they're not available for this
      match. Are you sure you want to select this squad?
    </p>
  ),
};

export const PickSquad: React.FC<Props> = ({ match }) => {
  const analytics = useAnalytics();

  const confirm = useConfirm();

  const [pickPlayersResponse, pickPlayers] = usePickPlayersMutation();

  const [teamPlayersResponse] = useTeamPlayersQuery({
    variables: { id: match.team.id },
  });

  const alert = useAlert();

  if (teamPlayersResponse.fetching) {
    return <Loader />;
  }

  const submitPickPlayers: FormikConfig<Fields>['onSubmit'] = async (
    values,
    actions,
  ) => {
    analytics.track('Form Submitted', { form: 'pick_squad' });

    const selectedPlayerIds = getSelectedKeys(values);

    const hasSelectedUnavailable = match.availabilities.some(
      ({ status, player }) =>
        status === 'UNAVAILABLE' && selectedPlayerIds.includes(player.id),
    );

    if (hasSelectedUnavailable && !(await confirm(confirmProps))) {
      analytics.track('Data Submit Failed', {
        form: 'pick_squad',
        reason: 'aborted_unavailable_player',
      });
      return;
    }

    pickPlayers({
      input: {
        matchId: match.id,
        playerIds: selectedPlayerIds,
      },
    }).then((res) => {
      pipe(
        O.fromNullable(res.data?.pickPlayers?.validation),
        O.map(formatErrors),
        O.fold(() => {
          analytics.track('Data Submit Succeeded', {
            form: 'pick_squad',
          });
          alert('Squad confirmed');
        }, actions.setErrors),
      );
    });
  };

  if (pickPlayersResponse.data?.pickPlayers?.entity) {
    return <Redirect to={paths.match.home(match.id)} />;
  }

  const teamPlayers = teamPlayersResponse.data?.team?.players;

  return isNil(teamPlayers) || A.isEmpty(teamPlayers) ? (
    <NoPlayersMessage />
  ) : (
    <PickSquadForm
      match={match}
      availablePlayers={teamPlayers}
      onSubmit={submitPickPlayers}
      loading={pickPlayersResponse.fetching}
    />
  );
};
